import React, { useEffect, useRef, useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

const LoadingIndicator = ({ label, fullScreen = false }) => (
  <div
    style={{
      position: fullScreen ? "fixed" : "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Spinner animation="border" role="status">
      <span className="sr-only">{label}</span>
    </Spinner>
  </div>
);

const LegalPdfViewer = ({ file, loading, error }) => {
  const viewerRef = useRef(null);
  const pageRefs = useRef([]);
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState(window.innerWidth);
  const [visiblePages, setVisiblePages] = useState(() => new Set());
  const [pageRatios, setPageRatios] = useState({});

  useEffect(() => {
    const updatePageWidth = () => setPageWidth(window.innerWidth);
    window.addEventListener("resize", updatePageWidth);
    return () => window.removeEventListener("resize", updatePageWidth);
  }, []);

  useEffect(() => {
    setNumPages(0);
    setVisiblePages(new Set());
    setPageRatios({});
  }, [file]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !numPages) return undefined;

    if (!window.IntersectionObserver) {
      setVisiblePages(new Set(Array.from({ length: numPages }, (_, index) => index + 1)));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setVisiblePages((currentPages) => {
          const nextPages = new Set(currentPages);
          entries.forEach((entry) => {
            const pageNumber = Number(entry.target.getAttribute("data-page-number"));
            if (entry.isIntersecting) {
              nextPages.add(pageNumber);
            } else {
              nextPages.delete(pageNumber);
            }
          });
          return nextPages;
        });
      },
      {
        root: viewer,
        rootMargin: `${viewer.clientHeight}px 0px`,
      }
    );

    pageRefs.current.slice(0, numPages).forEach((page) => {
      if (page) observer.observe(page);
    });

    return () => observer.disconnect();
  }, [numPages]);

  return (
    <div
      ref={viewerRef}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        overflowY: "auto",
        backgroundColor: "#e5e5e5",
        zIndex: 9999,
      }}
    >
      <Document
        file={file}
        onLoadSuccess={({ numPages: totalPages }) => {
          pageRefs.current = [];
          setVisiblePages(new Set());
          setNumPages(totalPages);
        }}
        loading={<LoadingIndicator label={loading} fullScreen />}
        error={error}
      >
        {Array.from({ length: numPages }, (_, index) => (
          <div
            key={index + 1}
            ref={(element) => {
              pageRefs.current[index] = element;
            }}
            data-page-number={index + 1}
            style={{
              position: "relative",
              width: pageWidth,
              height: pageWidth / (pageRatios[index + 1] || pageRatios[1] || 0.707),
              margin: "12px 0",
            }}
          >
            {visiblePages.has(index + 1) && (
              <Page
                pageNumber={index + 1}
                width={pageWidth}
                loading={<LoadingIndicator label={loading} />}
                renderAnnotationLayer={false}
                onLoadSuccess={({ width, height }) => {
                  const ratio = width / height;
                  setPageRatios((currentRatios) =>
                    currentRatios[index + 1] === ratio
                      ? currentRatios
                      : { ...currentRatios, [index + 1]: ratio }
                  );
                }}
              />
            )}
          </div>
        ))}
      </Document>
    </div>
  );
};

export default LegalPdfViewer;
